/**
 * RETRYING AN IMAGE-DECK CARD FACE.
 *
 * HISTORY (2026-08-31): the three image decks fetched each face as a plain `<img>` at first
 * render, MID-GAME — nothing preloaded `/decks/`. On the Iranian mobile paths this app mostly
 * serves, a large HTTPS response is severed mid-transfer, and a severed face used to paint a
 * blank WHITE card, silently, for as long as that element lived: measured 100.0% white against
 * 48.7–87.5% for the cards beside it. The `standard` deck never had this problem because its
 * artwork is a chunk with five attempts, backoff and a recovery screen (`card-faces.ts`); the
 * image decks were simply outside that pipeline.
 *
 * ⚠️⚠️ THE RETRY CANNOT REUSE THE SAME URL — the same lesson `card-faces.ts` records for
 * `import()`, and it is if anything sharper for images: assigning an `<img>` the `src` it already
 * has is not a state change at all, so the browser issues NO request, and even forcing one can be
 * answered from the cached failure. So attempt 0 is the plain URL — the happy path stays an
 * ordinary, cacheable request — and every retry carries a distinct query.
 *
 * Deliberately framework-free (the `card-faces.ts` split). Since 2026-10-20 its one user is the deck
 * loader (`deck-assets.ts`), which fetches and decodes a whole deck before any card draws it.
 */

/** Retries AFTER attempt 0, so a face is fetched at most `MAX_FACE_ATTEMPTS + 1` times. */
export const MAX_FACE_ATTEMPTS = 4;

const BASE_DELAY_MS = 400;
const MAX_DELAY_MS = 4000;

/**
 * The URL for one attempt. Attempt 0 is the file itself, so a healthy device makes exactly the
 * request it always made and the CDN/browser cache keys stay untouched.
 */
export function faceAttemptSrc(src: string, attempt: number): string {
  if (attempt <= 0) return src;
  return `${src}${src.includes("?") ? "&" : "?"}retry=${attempt}`;
}

/**
 * Exponential backoff with jitter — the same shape and constants as `card-faces.ts`. The jitter
 * matters because a dealt hand fails as a BATCH: 13 faces are fetched at once, so a fixed delay
 * would re-fire all of them into the same instant of a link that has just proved it cannot carry
 * them.
 */
export function faceRetryDelayMs(attempt: number, rnd: () => number = Math.random): number {
  const backoff = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
  return backoff * (0.7 + rnd() * 0.6);
}
